import React, { useContext, useEffect, useRef, useState } from "react";
import TreatmentHistory from "./TreatmentHistory/TreatmentHistory";
import UploadIcon from "../Images/Upload.svg";
import Diagnose from "../Images/Diagnose.svg";
import Tratment from "../Images/Treatment.svg";
import Mediaction from "../Images/Medication.svg";
import Referder from "../Images/Reffered.svg";
import { Container } from "@mui/material";
import "./TreatmentModule.css";
import Header from "../Header/Header";
import { Grid, Button } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DiagnosticDetails from "./DiagnosticDetails/DiagnosticDetails";
import ClinicalStage from "./ClinicalStage/ClinicalStage";
import ChemotherapyProtocol from "./ChemotherapyProtocol/ChemotherapyProtocol";
import Referred from "./Referred/Referred";
import { useLocation, useNavigate } from "react-router-dom";
import { CommonContext } from "../../context/CommonContext";
import { apisPath, paths } from "../../utils/path";
import { authHttp } from "../../services/commonHttpService";
import { calculateAge, formatDate } from "../../helpers/commonHelper";
import PatientCard from "../PatientCard/PatientCard";

const steps = [
  {
    title: "Diagnostic Details",
    description:
      "Upload clinical data, diagnosis reports, and treatment documentation here. \n Supported formats: PDF, JPG, PNG, JPEG",
    icon: <img src={UploadIcon} alt="Upload" width={40} height={40} />,
    component: DiagnosticDetails,
    workflowId: 2,
  },
  {
    title: "Clinical Stage",
    description: "Pre & Post Diagnosis Vitals of Patient Treatment Completion.",
    icon: <img src={Diagnose} alt="Upload" width={30} height={30} />,
    component: ClinicalStage,
    workflowId: 3,
  },
  {
    title: "Treatment History",
    description: "Manage patient treatments, and procedures.",
    icon: <img src={Tratment} alt="Upload" width={30} height={30} />,
    component: TreatmentHistory,
    workflowId: 4,
  },
  {
    title: "Current Chemotherapy Protocol",
    description: "Prescribe and administer medication.",
    icon: <img src={Mediaction} alt="Upload" width={30} height={30} />,
    component: ChemotherapyProtocol,
    workflowId: 5,
  },
  {
    title: "Instruction for DCCC while referring Patients",
    description: "Referrals from partner institutions.",
    icon: <img src={Referder} alt="Upload" width={30} height={30} />,
    component: Referred,
    workflowId: 6,
  },
];

const TreatmentModule = () => {
  const scrollParentRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { setIsLoading, setAlertState, setSnackState } = useContext(CommonContext);

  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [patientData, setPatientData] = useState(null);
  const [submitForm, setSubmitForm] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false); // Prevent rendering before workflow_id is set
  const patientId = location.state?.patientId;

  const StepComponent = steps[currentStep].component;

  // Fetch patient details
  const fetchPatientDetails = async () => {
    setIsLoading(true);

    try {
      const response = await authHttp.get(`${apisPath?.patientDetails}/${patientId}`);

      if (response?.data?.status) {
        const data = response?.data?.data;
        setPatientData(data);

        if (!patientId) return;

        // Get workflow_id from API response
        const workflowId = data?.workflow_id;

        // Find the step index that matches the workflow_id
        const stepIndex = steps.findIndex(step => step?.workflowId === workflowId);

        // If valid workflow_id found, set current step and mark previous steps as completed
        if (stepIndex !== -1) {
          setCurrentStep(stepIndex);

          // Mark all previous steps as completed
          if (stepIndex > 0) {
            setCompletedSteps(Array.from({ length: stepIndex }, (_, i) => i));
          }
        } else {
          // Default to first step if workflow_id not found (fallback)
          setCurrentStep(0);
          setCompletedSteps([]);
        }

        // Mark data as loaded after setting the correct step
        setIsDataLoaded(true);
      }
    } catch (error) {
      setSnackState({
        open: true,
        message: error?.response?.data?.message,
        severity: "error",
      });
      setIsDataLoaded(true); // Set to true even on error to prevent infinite loading
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const el = document.querySelector(".flex-grow-1.overflow-auto.px-3");
    if (el) {
      scrollParentRef.current = el;
    }
  }, []);

  const scrollToTop = () => {
    if (scrollParentRef.current) {
      scrollParentRef.current.scrollTop = 0;     // HARD RESET
    }
  };

  useEffect(() => {
    if (!patientId) {
      setAlertState({
        open: true,
        title: "Patient ID not found. Please select a patient from the beneficiary list.",
        btnTxt: "GO BACK",
        status: "fail",
      });
      navigate(`${paths?.user?.commonPoint}${paths?.user?.beneficiaryManagement}`);
    }
  }, [patientId]);


  // Fetch patient details on component mount
  useEffect(() => {
    if (patientId) {
      fetchPatientDetails();
    }
  }, [patientId]);


  // Handle next step - triggers form submission in child component
  const handleNext = () => {
    setSubmitForm(true);
  };

  // Child callback result
  const onFormResult = (success) => {
    if (success) {
      setCompletedSteps((prev) => [...prev, currentStep]);

      if (currentStep < steps.length - 1) {
        setCurrentStep((prev) => prev + 1);
      } else {
        setSnackState({
          open: true,
          message: "Treatment workflow completed successfully!",
          severity: "success",
        });
      }
    }

    setSubmitForm(false);  // Reset after each submit
  };

  useEffect(() => {
    scrollToTop();
  }, [currentStep]);

  return (
    <>
      <Header showFullHeader={false} />
      <Container>

        <div className=" my-4">
          <h5 className="mb-4 margin-top">Treatment & Procedure Module</h5>

          <div className="row">
            {/* Patient Card */}
            <div className="col-md-3 col-12 mb-3">
              <PatientCard patientId={patientId} />
            </div>

            {/* Workflow Steps */}
            <div className="col-md-9 col-12">
              <div className="row g-3">
                <h5 className="sub-header">Treatment Workflow Process</h5>
                {steps.map((step, index) => (
                  <div className="col-12 col-sm-6 col-md-2 w-19" key={index}>
                    <div
                      className={`card workflow-card shadow-sm p-2 h-100 ${completedSteps.includes(index) ? "completed-step" : ""
                        } ${currentStep === index ? "active-step" : ""}`}
                    // onClick={() => setCurrentStep(index)}
                    >
                      <div className="d-flex align-item-center gap-1">
                        {step.icon}
                        <strong className="header-tiles">{step.title}</strong>
                      </div>
                      <p className="font-para-dis mb-0">
                        {step.description || ""}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Render Current Step */}
          <div className="card shadow-sm mt-4">
            <div className="card-body">
              {/* Only render step component after data is loaded */}
              {isDataLoaded ? (
                <>
                  <StepComponent
                    patientId={patientId}
                    patientData={patientData}
                    submitForm={submitForm}       //  send submit toggle
                    formResult={onFormResult}      //  child callback
                    currentStep={currentStep}
                  />

                  <div className="d-flex justify-content-end mt-4 gap-2">
                    {/* Previous Button */}
                    {currentStep > 0 && (
                      <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{ borderRadius: "20px", px: 3 }}
                        onClick={() => {
                          setCurrentStep(currentStep - 1);
                          scrollToTop(); // Scroll to top
                        }}
                      >
                        Previous
                      </Button>
                    )}

                    {/* Next/Submit Button */}
                    {currentStep < steps.length - 1 ? (
                      <Button
                        variant="contained"
                        sx={{ borderRadius: "20px", px: 3 }}
                        onClick={handleNext}
                      >
                        Next →
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        sx={{ borderRadius: "20px", px: 3 }}
                        onClick={handleNext}
                      >
                        Submit
                      </Button>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-5">
                  {/* <p>Loading patient data...</p> */}
                </div>
              )}
            </div>
          </div>
        </div>
      </Container>
    </>
  );
};

export default TreatmentModule;
